import * as React from "react";
import clsx from "clsx";
import { ChevronRight, Eye, Folder, LayoutTemplate } from "lucide-react";
import { Link } from "react-router";

import { UserAvatar } from "@/components/ui/userAvatar";
import { getUserDisplayName } from "@/helpers/userDisplay";
import type { SnapshotViewTemplateGroup } from "@/modules/snapshots/snapshotViews.types";
import type { DirectoryUser } from "@/types/user";
import { SnapshotViewPinButton } from "./SnapshotViewPinButton";

interface Props {
  groups: SnapshotViewTemplateGroup[];
  ownersById: Map<string, DirectoryUser>;
  formatDateTime: (value: string) => string;
}

export const SnapshotViewsHierarchy = ({ groups, ownersById, formatDateTime }: Props) => {
  const [collapsedTemplates, setCollapsedTemplates] = React.useState<Set<string>>(() => new Set());
  const [collapsedCollections, setCollapsedCollections] = React.useState<Set<string>>(() => new Set());

  React.useEffect(() => {
    setCollapsedTemplates(new Set());
    setCollapsedCollections(new Set());
  }, [groups]);

  const toggle = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) => {
    setter(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <table className="w-full text-left text-sm">
      <thead className="border-b border-[#ebe5de] bg-[#fffdfb] text-xs uppercase tracking-wide text-gray-500">
        <tr>
          <th className="px-5 py-3">View</th>
          <th className="px-5 py-3">Owner</th>
          <th className="px-5 py-3">Access</th>
          <th className="px-5 py-3">Created</th>
          <th className="px-5 py-3">Last modified</th>
          <th className="w-16 px-5 py-3 text-center">Pin</th>
        </tr>
      </thead>
      {groups.map(template => {
        const templateExpanded = !collapsedTemplates.has(template.id);
        return (
          <tbody key={template.id} className="border-b border-[#e4ddd4] last:border-b-0">
            <tr className="bg-[#f5efe8]">
              <td colSpan={6} className="px-4 py-2.5">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 text-left font-semibold text-[#111b35]"
                  aria-expanded={templateExpanded}
                  aria-label={`${templateExpanded ? "Collapse" : "Expand"} template ${template.name}`}
                  onClick={() => toggle(setCollapsedTemplates, template.id)}
                >
                  <ChevronRight
                    className={clsx("h-4 w-4 transition-transform", { "rotate-90": templateExpanded })}
                    aria-hidden="true"
                  />
                  <LayoutTemplate className="h-4 w-4 text-coral" aria-hidden="true" />
                  <span>{template.name}</span>
                  <span className="ml-1 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-600">
                    {template.viewCount} on this page
                  </span>
                </button>
              </td>
            </tr>
            {templateExpanded &&
              template.collections.map(collection => {
                const collectionKey = `${template.id}:${collection.id}`;
                const collectionExpanded = !collapsedCollections.has(collectionKey);
                return (
                  <React.Fragment key={collectionKey}>
                    <tr className="border-t border-[#ebe5de] bg-[#fcfaf7]">
                      <td colSpan={6} className="py-2 pl-9 pr-4">
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 text-left font-medium text-gray-800"
                          aria-expanded={collectionExpanded}
                          aria-label={`${collectionExpanded ? "Collapse" : "Expand"} collection ${collection.name}`}
                          onClick={() => toggle(setCollapsedCollections, collectionKey)}
                        >
                          <ChevronRight
                            className={clsx("h-3.5 w-3.5 transition-transform", {
                              "rotate-90": collectionExpanded,
                            })}
                            aria-hidden="true"
                          />
                          <Folder className="h-4 w-4 text-[#a97936]" aria-hidden="true" />
                          <span>{collection.name}</span>
                          <span className="ml-1 text-xs font-normal text-gray-500">
                            {collection.viewCount} on this page
                          </span>
                        </button>
                      </td>
                    </tr>
                    {collectionExpanded &&
                      collection.views.map(view => {
                        const owner = ownersById.get(view.owner_id);
                        return (
                          <tr key={view.id} className="border-t border-[#f0ebe5] hover:bg-[#fffdfb]">
                            <td className="py-3.5 pl-16 pr-5 font-semibold text-[#111b35]">
                              <Link
                                className="inline-flex items-center gap-2 hover:underline"
                                to={`/snapshots/views/${view.id}`}
                              >
                                <Eye className="h-4 w-4 text-coral" aria-hidden="true" />
                                {view.name}
                              </Link>
                            </td>
                            <td className="px-5 py-3.5 text-gray-700">
                              <div className="flex min-w-[240px] items-center gap-3">
                                <UserAvatar
                                  user={owner}
                                  identifier={view.owner_id}
                                  size="lg"
                                  className="border-2 border-white shadow-sm"
                                />
                                {owner ? (
                                  <div className="flex min-w-0 flex-col items-start">
                                    <span className="line-clamp-1 text-sm font-medium text-gray-900">
                                      {getUserDisplayName(owner)}
                                    </span>
                                    <span className="line-clamp-1 text-xs text-gray-500">{owner.email}</span>
                                  </div>
                                ) : (
                                  <span className="truncate text-sm text-gray-700">{view.owner_id}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-5 py-3.5 capitalize text-gray-700">{view.access}</td>
                            <td className="px-5 py-3.5 text-gray-600">{formatDateTime(view.created_at)}</td>
                            <td className="px-5 py-3.5 text-gray-600">{formatDateTime(view.updated_at)}</td>
                            <td className="px-5 py-3.5 text-center">
                              <SnapshotViewPinButton view={view} compact />
                            </td>
                          </tr>
                        );
                      })}
                  </React.Fragment>
                );
              })}
          </tbody>
        );
      })}
    </table>
  );
};
